import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  LayoutDashboard, FolderKanban, CheckSquare, Users, BarChart3,
  Bell, Settings, LogOut, Plus, Search, Calendar, Clock, AlertTriangle,
  ChevronRight, ChevronLeft, ChevronDown, HelpCircle, ListTodo, X, Menu, Shield, Brain, FileText, Scale, BookOpen, Loader2,
  Building2, CheckCircle2, TrendingUp, Sparkles, ArrowUpRight, ArrowRight, Mail
} from 'lucide-react';
import { useAuth } from './context/AuthContext';
import { authAPI, dashboardAPI } from './api';
import {
  formatDate, getAvatarColor, getInitials,
  STATUS_CONFIG, PRIORITY_CONFIG, ROLE_LABELS, getProgressColor, isOverdue
} from './utils/helpers';
import './styles/global.css';
import AppSwitcher from './AppSwitcher';

// =============================================
// REUSABLE COMPONENTS
// =============================  ================

const Avatar = ({ firstName, lastName, size = '', className = '' }) => (
  <div className={`avatar ${size} ${className}`} style={{ background: getAvatarColor(firstName) }}>
    {getInitials(firstName, lastName)}
  </div>
);

const StatusBadge = ({ status, type = 'status' }) => {
  const config = type === 'priority' ? PRIORITY_CONFIG[status] : STATUS_CONFIG[status];
  if (!config) return <span className="badge badge-todo">{status}</span>;
  return <span className={`badge ${config.class}`}>{config.label}</span>;
};

const ProgressBar = ({ value = 0 }) => (
  <div className="progress-bar">
    <div className={`progress-fill ${getProgressColor(value)}`} style={{ width: `${Math.min(value, 100)}%` }} />
  </div>
);

const Loader = () => <div className="page-loader"><div className="spinner" /></div>;

const EmptyState = ({ icon: Icon = FolderKanban, title, message, action }) => (
  <div className="empty-state">
    <Icon size={48} />
    <h3>{title}</h3>
    <p>{message}</p>
    {action && <div style={{ marginTop: 16 }}>{action}</div>}
  </div>
);

const Modal = ({ isOpen, onClose, title, header, children, footer, size = '', showClose = true }) => {
  if (!isOpen) return null;
  const hasHeader = title || header || (onClose && showClose);
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal ${size}`} onClick={e => e.stopPropagation()}>
        {hasHeader && (
          <div className="modal-header" style={!title && !header ? { justifyContent: 'flex-end', borderBottom: 'none', padding: '16px 20px 0' } : {}}>
            {header ? header : title ? <h3>{title}</h3> : null}
            {onClose && showClose && <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={18} /></button>}
          </div>
        )}
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
};

const Pagination = ({ page, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;
  return (
    <div className="pagination">
      <button className="page-btn" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>Prev</button>
      {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
        const p = page <= 3 ? i + 1 : page - 2 + i;
        if (p > totalPages) return null;
        return <button key={p} className={`page-btn ${p === page ? 'active' : ''}`} onClick={() => onPageChange(p)}>{p}</button>;
      })}
      <button className="page-btn" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>Next</button>
    </div>
  );
};

// =============================================
// SIDEBAR NAVIGATION
// =============================================

const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout, unreadCount, hasRole } = useAuth();
  const location = useLocation();

  const navSections = [
    {
      title: 'Core Platform',
      items: [
        { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', isImportant: true },
        { path: '/projects', icon: FolderKanban, label: 'Projects', isImportant: true },
        { path: '/my-tasks', icon: CheckSquare, label: 'My Activities' },
        ...(hasRole('project_director', 'project_manager', 'site_engineer', 'client_rep') ? [{ path: '/team-tasks', icon: ListTodo, label: 'Site Activities' }] : []),
      ]
    },
    {
      title: 'Procurement & AI',
      items: [
        { path: '/rfp-dashboard', icon: Scale, label: 'RFP Dashboard' },
        { path: '/rfps', icon: FileText, label: 'RFP Builder' },
        { path: '/proposals', icon: BookOpen, label: 'Vendor Proposals' },
        { path: '/ai-assistant', icon: Brain, label: 'AI Assistant', badge: 'AI', isFeatureBadge: true },
      ]
    },
    {
      title: 'Insights & System',
      items: [
        { path: '/reports', icon: BarChart3, label: 'Reports' },
        ...(hasRole('project_director') ? [{ path: '/admin/users', icon: Shield, label: 'Admin' }] : []),
      ]
    }
  ];

  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const confirmLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
    } catch {
      setIsLoggingOut(false);
      setShowLogoutModal(false);
    }
  };

  const firstName = user?.first_name || user?.firstname || '';
  const lastName = user?.last_name || user?.lastname || '';
  const displayRole = ROLE_LABELS[user?.role] || user?.clientRole || user?.role || 'User';

  return (
    <>
      <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
        {/* Brand Header */}
        <div className="sidebar-brand">
          <Link to="/dashboard" className="sidebar-brand-link">
            <img src="/Propel-w.png" alt="Propel Logo" className="sidebar-brand-logo" />
          </Link>
        </div>

        {/* Main Navigation */}
        <nav className="sidebar-nav">
          {navSections.map((section, sIdx) => {
            const visibleItems = section.items;
            if (visibleItems.length === 0) return null;
            return (
              <div key={section.title || sIdx} className="nav-section">
                <div className="nav-section-label">{section.title}</div>
                {visibleItems.map(item => {
                  const isActive = location.pathname === item.path || (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
                  return (
                    <div key={item.path} className="nav-item-wrapper" data-tooltip={item.label}>
                      <Link
                        to={item.path}
                        className={`nav-item ${isActive ? 'active' : ''} ${item.isImportant ? 'important' : ''}`}
                        onClick={onClose}
                      >
                        <item.icon size={18} className="nav-icon" />
                        <span className="nav-label">{item.label}</span>
                        {item.badge && (
                          <span className={`nav-badge ${item.isFeatureBadge ? 'feature-badge' : ''}`}>
                            {item.badge}
                          </span>
                        )}
                      </Link>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* Bottom Area */}
        <div className="sidebar-bottom">
          <div className="sidebar-bottom-nav">
            <div className="nav-item-wrapper" data-tooltip="Help & Support">
              <div
                className="nav-item"
                onClick={() => setShowHelpModal(true)}
                role="button"
                tabIndex={0}
              >
                <HelpCircle size={18} className="nav-icon" />
                <span className="nav-label">Help</span>
              </div>
            </div>
            <div className="nav-item-wrapper" data-tooltip="Sign Out">
              <div className="nav-item" onClick={() => setShowLogoutModal(true)} role="button" tabIndex={0}>
                <LogOut size={18} className="nav-icon" />
                <span className="nav-label">Sign Out</span>
              </div>
            </div>
          </div>

          {/* Powered By ConvoLink */}
          <div
            style={{
              borderTop: '1px solid var(--sb-border)',
              padding: '10px 20px',
              display: 'flex',
              alignItems: 'flex-end',
              gap: 8
            }}
          >
            <span
              style={{
                fontSize: '0.62rem',
                fontWeight: 700,
                color: 'var(--sb-text-muted)',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}
            >
              Powered by
            </span>
            <img
              src="/convolink-light.png"
              alt="ConvoLink"
              style={{
                height: 26,
                width: 'auto',
                maxWidth: 120,
                objectFit: 'contain',
                objectPosition: 'left',
                display: 'block'
              }}
            />
          </div>
        </div>
      </aside>

      {/* In-App Help & Support Modal */}
      <Modal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        showClose={false}
        size="modal-md"
        header={
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%' }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 10,
                background: 'linear-gradient(135deg, rgba(0, 56, 167, 0.08) 0%, rgba(0, 175, 192, 0.12) 100%)',
                color: '#0038A7',
                border: '1px solid rgba(0, 56, 167, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <HelpCircle size={22} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#0B1220', letterSpacing: '-0.3px' }}>
                Help & Platform Support
              </h3>
              <p style={{ margin: '2px 0 0 0', fontSize: '0.82rem', color: 'var(--px-text-muted)' }}>
                Propel procurement platform guides, contact channels, and system documentation
              </p>
            </div>
          </div>
        }
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, width: '100%' }}>
            <button className="btn btn-primary" onClick={() => setShowHelpModal(false)} style={{ minWidth: 100 }}>
              Close
            </button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Quick Support Info */}
          <div
            style={{
              padding: '14px 16px',
              borderRadius: 10,
              background: '#f8fafc',
              border: '1px solid var(--px-border)',
              display: 'flex',
              flexDirection: 'column',
              gap: 8
            }}
          >
            <div style={{ fontWeight: 650, fontSize: '0.9rem', color: '#0b1220' }}>
              Direct Support Channels
            </div>
            <div style={{ fontSize: '0.84rem', color: 'var(--px-text-secondary)', lineHeight: 1.6 }}>
              Need technical assistance or encountering issues with tender evaluation and RFP generation?
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 4, flexWrap: 'wrap' }}>
              <div
                style={{
                  padding: '8px 12px',
                  borderRadius: 6,
                  background: '#ffffff',
                  border: '1px solid var(--px-border)',
                  fontSize: '0.8rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}
              >
                <Mail size={14} style={{ color: '#0038A7' }} />
                <span>support@propel.io</span>
              </div>
              <div
                style={{
                  padding: '8px 12px',
                  borderRadius: 6,
                  background: '#ffffff',
                  border: '1px solid var(--px-border)',
                  fontSize: '0.8rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}
              >
                <Clock size={14} style={{ color: '#059669' }} />
                <span>Mon – Fri, 9:00 AM – 6:00 PM GST</span>
              </div>
            </div>
          </div>

          {/* Quick Features Guide */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontWeight: 650, fontSize: '0.85rem', color: '#0b1220' }}>
              Quick Navigation Tips
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div style={{ padding: 12, borderRadius: 8, background: '#eff6ff', border: '1px solid #bfdbfe' }}>
                <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#1e3a8a', marginBottom: 2 }}>RFP Builder</div>
                <div style={{ fontSize: '0.78rem', color: '#1e40af', lineHeight: 1.4 }}>
                  Draft 22 FIDIC-aligned clauses with automated Propel AI synthesis.
                </div>
              </div>
              <div style={{ padding: 12, borderRadius: 8, background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#166534', marginBottom: 2 }}>Proposal Evaluation</div>
                <div style={{ fontSize: '0.78rem', color: '#15803d', lineHeight: 1.4 }}>
                  Side-by-side criteria benchmarking and AI technical scoring.
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Confirmation Modal */}
      <Modal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        showClose={false}
        size="modal-xs"
        footer={
          <div className="confirm-modal-actions">
            <button
              className="btn btn-secondary"
              onClick={() => setShowLogoutModal(false)}
              disabled={isLoggingOut}
            >
              Cancel
            </button>
            <button
              className="btn btn-danger"
              onClick={confirmLogout}
              disabled={isLoggingOut}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
            >
              {isLoggingOut ? (
                <>
                  <Loader2 size={16} className="btn-spinner" />
                  <span>Signing out...</span>
                </>
              ) : (
                <>
                  <LogOut size={16} />
                  <span>Sign Out</span>
                </>
              )}
            </button>
          </div>
        }
      >
        <div className="confirm-modal-content">
          <div className="confirm-modal-icon-wrapper">
            <LogOut size={24} />
          </div>
          <div className="confirm-modal-title">Sign out of PropelX?</div>
          <p className="confirm-modal-desc">
            You will need to sign back in to access your projects, tasks, and RFPs.
          </p>
          {user && (
            <div className="confirm-modal-user-pill">
              <Avatar firstName={firstName} lastName={lastName} size="sm" />
              <span>{firstName} {lastName}</span>
              <span style={{ color: 'var(--px-text-muted)' }}>•</span>
              <span style={{ color: 'var(--px-text-muted)', textTransform: 'capitalize' }}>{displayRole}</span>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

// =============================================
// APP LAYOUT
// =============================================

const AppLayout = ({ children }) => {
  const { user, unreadCount } = useAuth();
  const location = useLocation();
  const firstName = user?.first_name || user?.firstname || '';
  const lastName = user?.last_name || user?.lastname || '';
  const displayRole = ROLE_LABELS[user?.role] || user?.clientRole || user?.role || 'User';

  const isNotificationsActive = location.pathname === '/notifications';
  const isProfileActive = location.pathname === '/profile';

  return (
    <div className="app-layout">
      <Sidebar isOpen={false} onClose={() => { }} />
      <div className="main-content">
        <header className="top-bar">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          </div>

          <div className="top-bar-actions" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <AppSwitcher />
            <Link
              to="/notifications"
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 38,
                height: 38,
                borderRadius: 10,
                color: isNotificationsActive ? '#2563eb' : 'var(--px-text-secondary)',
                textDecoration: 'none',
                background: isNotificationsActive ? '#eff6ff' : 'var(--px-surface-hover, #f8fafc)',
                border: isNotificationsActive ? '1px solid #bfdbfe' : '1px solid var(--px-border, #e2e8f0)',
                boxShadow: isNotificationsActive ? '0 0 0 3px rgba(37, 99, 235, 0.12)' : 'none',
                transition: 'all 0.18s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
              title="Notifications"
              onMouseEnter={(e) => {
                if (!isNotificationsActive) {
                  e.currentTarget.style.background = '#f1f5f9';
                  e.currentTarget.style.borderColor = '#cbd5e1';
                }
              }}
              onMouseLeave={(e) => {
                if (!isNotificationsActive) {
                  e.currentTarget.style.background = 'var(--px-surface-hover, #f8fafc)';
                  e.currentTarget.style.borderColor = 'var(--px-border, #e2e8f0)';
                }
              }}
            >
              <Bell size={18} />
              {unreadCount > 0 && (
                <span
                  style={{
                    position: 'absolute',
                    top: -3,
                    right: -3,
                    minWidth: 16,
                    height: 16,
                    padding: '0 4px',
                    borderRadius: 8,
                    background: '#dc2626',
                    color: '#fff',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid #fff'
                  }}
                >
                  {unreadCount}
                </span>
              )}
            </Link>

            {user && (
              <Link
                to="/profile"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  textDecoration: 'none',
                  padding: '3px 12px 3px 4px',
                  borderRadius: 24,
                  background: isProfileActive ? '#eff6ff' : 'var(--px-surface-hover, #f8fafc)',
                  border: isProfileActive ? '1px solid #bfdbfe' : '1px solid var(--px-border, #e2e8f0)',
                  boxShadow: isProfileActive ? '0 0 0 3px rgba(37, 99, 235, 0.12)' : 'none',
                  transition: 'all 0.18s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
                title="View Profile"
                onMouseEnter={(e) => {
                  if (!isProfileActive) {
                    e.currentTarget.style.background = '#f1f5f9';
                    e.currentTarget.style.borderColor = '#cbd5e1';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isProfileActive) {
                    e.currentTarget.style.background = 'var(--px-surface-hover, #f8fafc)';
                    e.currentTarget.style.borderColor = 'var(--px-border, #e2e8f0)';
                  }
                }}
              >
                <Avatar
                  firstName={firstName}
                  lastName={lastName}
                  size="sm"
                  style={{ width: 32, height: 32, fontSize: '0.82rem', fontWeight: 700 }}
                />
                <div style={{ textAlign: 'left', paddingRight: 2 }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: isProfileActive ? '#1d4ed8' : '#0B1220', lineHeight: 1.2 }}>
                    {firstName} {lastName}
                  </div>
                  <div style={{ fontSize: '0.70rem', color: isProfileActive ? '#2563eb' : 'var(--px-text-muted)', lineHeight: 1.2, marginTop: 2, textTransform: 'capitalize' }}>
                    {displayRole}
                  </div>
                </div>
              </Link>
            )}
          </div>
        </header>
        <div className="page-content">{children}</div>
      </div>
    </div>
  );
};

// =============================================
// LOGIN PAGE
// =============================================

const LoginPage = () => {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const isLoggedOut = new URLSearchParams(window.location.search).get('logged_out') === 'true';

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  return (
    <div className="auth-page">
      <div className="auth-card" style={{ textAlign: 'center', padding: '36px 32px' }}>
        <div className="auth-brand" style={{ marginBottom: 20 }}>
          <img src="/Propel-w.png" alt="Propel Logo" style={{ height: '48px', marginBottom: '16px' }} />
          {isLoggedOut ? (
            <div style={{ padding: '12px 16px', background: 'rgba(5, 150, 105, 0.1)', color: '#059669', borderRadius: 8, fontSize: '0.88rem', fontWeight: 500, marginBottom: 16 }}>
              ✓ You have been successfully signed out
            </div>
          ) : (
            <p style={{ color: 'var(--px-text-secondary)', fontSize: '0.92rem' }}>
              Welcome to PropelX Infrastructure Platform
            </p>
          )}
        </div>
        <button className="btn btn-primary btn-lg" onClick={login} style={{ width: '100%', marginTop: 8 }}>
          Sign In with Central SSO
        </button>
      </div>
    </div>
  );
};

// =============================================
// ACCOUNT ACTIVATION PAGE
// =============================================

const ActivatePage = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const token = new URLSearchParams(useLocation().search).get('token');

  const handleActivate = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      const { data } = await authAPI.activate({ token, password });
      localStorage.setItem('propelx_token', data.token);
      toast.success('Account activated!');
      navigate('/dashboard');
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Activation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-brand"><h1>Activate Account</h1><p>Set your password to get started</p></div>
        <form onSubmit={handleActivate}>
          <div className="form-group">
            <label className="form-label">New Password</label>
            <input className="form-input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min. 8 characters" required />
          </div>
          <div className="form-group">
            <label className="form-label">Confirm Password</label>
            <input className="form-input" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm password" required />
          </div>
          <button className="btn btn-primary btn-lg" type="submit" disabled={loading} style={{ width: '100%' }}>
            {loading ? 'Activating...' : 'Activate Account'}
          </button>
        </form>
      </div>
    </div>
  );
};

// =============================================
// DASHBOARD PAGE
// =============================================

const DashboardPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await dashboardAPI.get();
        setData(d);
      } catch (err) {
        if (err?.response?.status !== 401) {
          toast.error('Failed to load dashboard');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading || !data) return <Loader />;

  const { kpis, projects_by_status, tasks_by_priority, recent_projects, upcoming_tasks } = data;

  const userFirstName = user?.first_name || user?.firstname || user?.name?.split(' ')?.[0] || 'there';

  return (
    <>
      {/* Modern Welcome Banner */}
      <div className="dashboard-welcome-banner">
        <div>
          <h2 className="dashboard-welcome-title">
            Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'}, {userFirstName}</h2>
          <div className="dashboard-welcome-subtitle">
            <span>Here's your site activities and project programme overview for today.</span>
          </div>
        </div>
      </div>

      {/* Modern KPI Cards with Icons */}
      <div className="kpi-grid">
        <div className="kpi-card kpi-primary">
          <div className="kpi-card-header">
            <span className="kpi-label">Active Sites</span>
            <div className="kpi-card-icon-pill">
              <Building2 size={16} />
            </div>
          </div>
          <div className="kpi-value">{kpis.active_projects}</div>
          <div className="kpi-card-footer">
            <span>Sites in active progress</span>
          </div>
        </div>

        <div className="kpi-card kpi-cyan">
          <div className="kpi-card-header">
            <span className="kpi-label">My Open Activities</span>
            <div className="kpi-card-icon-pill">
              <CheckSquare size={16} />
            </div>
          </div>
          <div className="kpi-value">{kpis.my_open_tasks}</div>
          <div className="kpi-card-footer">
            <span>Assigned to you</span>
          </div>
        </div>

        <div className="kpi-card kpi-warning">
          <div className="kpi-card-header">
            <span className="kpi-label">Due This Week</span>
            <div className="kpi-card-icon-pill">
              <Clock size={16} />
            </div>
          </div>
          <div className="kpi-value">{kpis.tasks_due_this_week}</div>
          <div className="kpi-card-footer">
            <span>Requires near-term action</span>
          </div>
        </div>

        <div className="kpi-card kpi-danger">
          <div className="kpi-card-header">
            <span className="kpi-label">Overdue Activities</span>
            <div className="kpi-card-icon-pill">
              <AlertTriangle size={16} />
            </div>
          </div>
          <div className="kpi-value">{kpis.overdue_tasks}</div>
          <div className="kpi-card-footer">
            <span>Critical schedule delay</span>
          </div>
        </div>

        <div className="kpi-card kpi-success">
          <div className="kpi-card-header">
            <span className="kpi-label">Completion Rate</span>
            <div className="kpi-card-icon-pill">
              <TrendingUp size={16} />
            </div>
          </div>
          <div className="kpi-value">{kpis.completion_rate}%</div>
          <div className="kpi-card-footer">
            <span>Across all active work</span>
          </div>
        </div>

        <div className="kpi-card kpi-muted">
          <div className="kpi-card-header">
            <span className="kpi-label">On Hold</span>
            <div className="kpi-card-icon-pill">
              <ListTodo size={16} />
            </div>
          </div>
          <div className="kpi-value">{kpis.blocked_tasks}</div>
          <div className="kpi-card-footer">
            <span>Activities pending clearance</span>
          </div>
        </div>
      </div>

      {/* Main Grid: Recent Projects & Upcoming Activities */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 0.9fr', gap: 20 }}>
        {/* Recent Projects */}
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">Recent Projects</div>
              <div className="card-subtitle">Sites currently tracked on the programme</div>
            </div>
            <Link to="/projects" className="btn btn-ghost btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 4, textDecoration: 'none' }}>
              <span>View all</span>
              <ArrowRight size={14} />
            </Link>
          </div>
          {recent_projects?.length === 0 ? (
            <EmptyState title="No projects" message="Create your first project" />
          ) : (
            <div style={{ marginTop: 10 }}>
              {recent_projects.map(p => (
                <div
                  key={p.id}
                  className="project-row-card"
                  onClick={() => navigate(`/projects/${p.id}`)}
                >
                  <div className="project-row-avatar">
                    <Building2 size={19} />
                  </div>
                  <div className="project-row-info">
                    <div className="project-row-name" title={p.name}>
                      {p.name}
                    </div>
                    <div className="project-row-code">
                      {p.project_code}
                    </div>
                  </div>
                  <div className="project-row-status">
                    <StatusBadge status={p.status} />
                  </div>
                  <div className="project-row-progress">
                    <div className="project-progress-meta">
                      <span className="project-progress-val">{p.progress}%</span>
                    </div>
                    <div className="project-progress-bar">
                      <div
                        className="project-progress-bar-fill"
                        style={{ width: `${Math.min(p.progress || 0, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* My Upcoming Activities */}
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">My Upcoming Activities</div>
              <div className="card-subtitle">Directly assigned milestones and site tasks</div>
            </div>
            <Link to="/my-tasks" className="btn btn-ghost btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 4, textDecoration: 'none' }}>
              <span>View all</span>
              <ArrowRight size={14} />
            </Link>
          </div>
          {upcoming_tasks?.length === 0 ? (
            <EmptyState title="All clear!" message="No pending activities on your schedule" />
          ) : (
            <div style={{ marginTop: 10 }}>
              {upcoming_tasks.map(t => {
                const overdue = t.due_date && isOverdue(t.due_date);
                return (
                  <div
                    key={t.id}
                    className="task-row-card"
                    onClick={() => navigate(`/projects/${t.project_id}`)}
                  >
                    <div className="task-row-icon">
                      <CheckSquare size={18} />
                    </div>
                    <div className="task-row-info">
                      <div className="task-row-title" title={t.title}>
                        {t.title}
                      </div>
                      <div className="task-row-project">
                        {t.project?.name || 'Site Programme'}
                      </div>
                    </div>
                    <div className="task-row-meta">
                      <StatusBadge status={t.priority} type="priority" />
                      {t.due_date && (
                        <span className={`task-due-badge ${overdue ? 'overdue' : ''}`}>
                          <Calendar size={12} />
                          <span>{formatDate(t.due_date)}</span>
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* AI Assistant Banner */}
      <div className="ai-assistant-card">
        <div className="ai-assistant-content">
          <div className="ai-assistant-icon-box">
            <Brain size={22} />
          </div>
          <div>
            <div className="ai-assistant-title">
              <span>Propel AI Assistant</span>
              <span className="ai-assistant-badge">PRO</span>
            </div>
            <div className="ai-assistant-desc">
              Get project summaries, programme delay analysis, and smart recommendations powered by Propel AI.
            </div>
          </div>
        </div>
        <Link to="/ai-assistant" className="ai-assistant-cta">
          <Sparkles size={15} />
          <span>Open AI Assistant</span>
          <ArrowRight size={14} />
        </Link>
      </div>
    </>
  );
};

export { LoginPage, ActivatePage, DashboardPage, AppLayout, Modal, Avatar, StatusBadge, ProgressBar, Loader, EmptyState, Pagination };
